/**
 * Collapsible fieldset — a <fieldset> whose <legend> toggles the body open/shut.
 *
 * Two entry points:
 *  - `createCollapsibleFieldset(...)` builds the whole element for you.
 *  - `wireCollapsible(fieldsetEl, ...)` attaches the toggle behaviour to markup
 *    you've already rendered (e.g. from an HTML-string template) that follows the
 *    class contract below.
 *
 * Markup contract (see components/collapsible-fieldset.css):
 *   <fieldset class="collapsible [collapsed] [collapsible--feature]">
 *     <legend class="collapsible-legend" role="button" tabindex="0"
 *             aria-expanded="…">…<i class="collapsible-legend__caret"></i></legend>
 *     <div class="collapsible-content"><div class="collapsible-inner">…</div></div>
 *   </fieldset>
 *
 * The helper owns ONLY the open/close mechanics (the `collapsed` class,
 * `aria-expanded`, click + Enter/Space). Any content-specific behaviour (e.g.
 * clearing errors on collapse) stays with the caller via the `onToggle` hook.
 */

import './collapsible-fieldset.css';

/**
 * Attach toggle behaviour to an existing `.collapsible` fieldset.
 * @param {HTMLElement} fieldset - the `.collapsible` element.
 * @param {object} [opts]
 * @param {boolean} [opts.expanded=false] - initial state.
 * @param {(expanded: boolean) => void} [opts.onToggle] - called after each change.
 * @returns {{ setExpanded: (v:boolean)=>void, isExpanded: ()=>boolean, legend: HTMLElement }}
 */
export function wireCollapsible(fieldset, { expanded = false, onToggle } = {}) {
  const legend = fieldset.querySelector('.collapsible-legend');

  const setExpanded = (value) => {
    fieldset.classList.toggle('collapsed', !value);
    legend?.setAttribute('aria-expanded', String(value));
    onToggle?.(value);
  };
  const isExpanded = () => !fieldset.classList.contains('collapsed');

  setExpanded(expanded);

  const toggle = () => setExpanded(!isExpanded());
  legend?.addEventListener('click', toggle);
  legend?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  });

  return { setExpanded, isExpanded, legend };
}

/**
 * Build a collapsible fieldset element.
 * @param {object} opts
 * @param {string} opts.legendHtml - inner HTML for the legend (before the caret).
 * @param {Node|string} opts.content - the body (inside `.collapsible-inner`).
 * @param {boolean} [opts.expanded=false] - initial state.
 * @param {boolean} [opts.feature=false] - apply the brand `--feature` styling.
 * @param {(expanded: boolean) => void} [opts.onToggle]
 * @returns {{ fieldset: HTMLFieldSetElement, setExpanded, isExpanded, legend }}
 */
export function createCollapsibleFieldset({
  legendHtml = '',
  content = '',
  expanded = false,
  feature = false,
  onToggle,
} = {}) {
  const fieldset = document.createElement('fieldset');
  fieldset.className = feature ? 'collapsible collapsible--feature' : 'collapsible';

  const legend = document.createElement('legend');
  legend.className = 'collapsible-legend';
  legend.setAttribute('role', 'button');
  legend.setAttribute('tabindex', '0');
  legend.innerHTML = `${legendHtml}<i class="fa-solid fa-chevron-down collapsible-legend__caret" aria-hidden="true"></i>`;

  const contentEl = document.createElement('div');
  contentEl.className = 'collapsible-content';
  const inner = document.createElement('div');
  inner.className = 'collapsible-inner';
  if (typeof content === 'string') inner.innerHTML = content;
  else if (content) inner.appendChild(content);
  contentEl.appendChild(inner);

  fieldset.append(legend, contentEl);

  const api = wireCollapsible(fieldset, { expanded, onToggle });
  return { fieldset, ...api };
}
